'use client'

import { useCallback, useRef, useState } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { DecodeHintType } from '@zxing/library'
import { CalculatorCard, InfoBox, Disclaimer, SegmentedField } from './CalculatorUI'
import { CameraScanner, ResultPanel, UploadBox } from './ScannerShared'

export default function BarcodeScanner() {
  const [mode, setMode] = useState('camera')
  const [preview, setPreview] = useState('')
  const [result, setResult] = useState('')
  const [error, setError] = useState('')
  const readerRef = useRef(null)

  const getReader = () => {
    if (!readerRef.current) {
      // "Try harder" is slower but reads small, skewed or low-contrast barcodes far more reliably.
      const hints = new Map([[DecodeHintType.TRY_HARDER, true]])
      readerRef.current = new BrowserMultiFormatReader(hints)
    }
    return readerRef.current
  }

  const decodeFrame = useCallback((canvas) => {
    try {
      return getReader().decodeFromCanvas(canvas).getText()
    } catch {
      return null // nothing readable in this frame yet
    }
  }, [])

  const reset = () => {
    setPreview('')
    setResult('')
    setError('')
  }

  const handleFile = useCallback((file) => {
    if (!file) return
    setResult('')
    setError('')
    const reader = new FileReader()
    reader.onload = (e) => {
      const imgUrl = e.target.result
      setPreview(imgUrl)
      const img = new Image()
      img.onload = async () => {
        try {
          const decoded = await getReader().decodeFromImageElement(img)
          setResult(decoded.getText())
        } catch {
          setError('No barcode could be found in this image. Try a clearer, closer, well-lit photo.')
        }
      }
      img.onerror = () => setError('This file could not be read as an image.')
      img.src = imgUrl
    }
    reader.readAsDataURL(file)
  }, [])

  const handleCameraResult = useCallback((text) => {
    setPreview('')
    setError('')
    setResult(text)
  }, [])

  return (
    <CalculatorCard>
      <p className="text-xs font-bold uppercase tracking-wider text-[#0b2540]">Scan a barcode with your camera or from an image</p>

      <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:gap-8">
        <div className="min-w-0 space-y-5">
          <SegmentedField
            value={mode}
            onChange={(next) => {
              setMode(next)
              reset()
            }}
            options={[{ value: 'camera', label: 'Use camera' }, { value: 'upload', label: 'Upload image' }]}
          />

          {mode === 'camera' ? (
            <CameraScanner decodeFrame={decodeFrame} onResult={handleCameraResult} onPhoto={handleFile} instruction="Point your camera at a barcode and hold it steady." />
          ) : (
            <UploadBox onFile={handleFile} noun="barcode" />
          )}

          <InfoBox
            items={[
              'Reads CODE128, EAN-13, UPC-A, CODE39 and most common 1D barcode formats, plus QR codes.',
              'Everything is decoded on your device — no image or video is uploaded anywhere.',
              'If the camera is blocked, we will show you how to allow it — or just upload a photo instead.',
            ]}
          />
        </div>

        <ResultPanel preview={preview} result={result} error={error} label="Decoded value" onReset={reset} isLink={false} />
      </div>

      <Disclaimer>Camera scanning needs browser permission and works best in good lighting.</Disclaimer>
    </CalculatorCard>
  )
}
